import * as React from 'react';

import {TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';

import {staticPageList} from '@packages/shared/utils/PageManagerUtils';
import {createSelector} from 'reselect';

import {TText, EText} from '@packages/systems/wfdl-ast';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  selectCurrentLocale,
  selectIsAllowedToDraftPagesPerLocale,
} from '@packages/systems/localization';
import {isDraftPage, isHomePage} from '@packages/shared/utils/PageUtils';
import {List} from 'immutable';
import {PageListNodeType} from '@packages/systems/core/records/PageRecord';
import {getPageNodes, nodeAncestry} from '@packages/shared/utils/PageTreeUtils';
import {
  PageOptionType,
  PageSelectorPopover,
  getPagePath,
  getPageType,
} from '@packages/systems/designer/page-select';
import {isMint} from '@packages/systems/core/utils/RecordUtils';
import {ChangeTypes} from './Link/LinkTypes';

const selectNewStaticPageList = createSelector(
  (state: DesignerStores) => state.PageStore,
  (state) => state.PageManagerUIStore,
  (state) => selectCurrentLocale(state.LocalizationStore),
  selectIsAllowedToDraftPagesPerLocale,
  (
    pageState,
    pageManagerState,
    currentLocale,
    isAllowedToDraftPagesPerLocale
  ) => {
    const staticPages: List<PageListNodeType> = staticPageList(
      pageState,
      pageManagerState,
      currentLocale,
      isAllowedToDraftPagesPerLocale,
      skip
    );

    const pages = getPageNodes(pageState);

    // Filter out standalone branch parent pages and static templates
    return staticPages
      .toArray()
      .filter((page) => {
        return page.type !== 'Folder' && !isMint(page);
      })
      .map((page) => {
        const isDraft = isDraftPage(page, {
          currentLocale,
          canDraftPagesPerLocale: isAllowedToDraftPagesPerLocale,
        });

        const ancestry = nodeAncestry(page.id, pages);
        const ancestryPath = getPagePath(ancestry);
        return {
          ancestry: ancestryPath,
          pageType: getPageType({
            isHomePage: isHomePage(page),
            isDraft,
          }),
          name: page.get('name'),
          id: page.get('id'),
        };
      }) as PageOptionType[];
  }
);

const skip = {
  includeUsysUtilityPages: true,
  skipNestedInFolder: false,
  skipUtilityPages: true,
  skipBranchPages: true,
  skipStandaloneBranchPageParents: true,
  skipStaticTemplates: true,
} as const;

export const LinkPageSelectorRender = ({
  pageId,
  onChange,
}: {
  pageId: string | null | undefined;
  onChange: (
    newPageId: string | null | undefined,
    arg2: {
      changeType: ChangeTypes;
    }
  ) => void;
}) => {
  const newPages = useDesignerSelector(selectNewStaticPageList);

  /**
   * Compatibility callback around the `onChange` function
   * to ensure it passes the expected `changeType` argument.
   */
  const newOnChange = (newPageId: string | null | undefined) => {
    onChange(newPageId, {changeType: 'changeEnd'});
  };

  return (
    <PageSelectorPopover
      value={pageId}
      options={newPages}
      handlePageSelectorClick={newOnChange}
    />
  );
};

/**
 * We utilize the Page mode dropdown selector as a type for User Systems so we
 * share the `LinkPageSelectorRender` between it and the Custom `Link` type.
 */
export const LinkPageSelectorType = TypeImplementation.Alias({
  type: TText,
  render: ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    const onChange = (
      newPageId: string | null | undefined,
      {
        changeType,
      }: {
        changeType: ChangeTypes;
      }
    ) => {
      if (changeType === 'changeEnd') {
        if (newPageId && newPageId !== pageId) {
          dispatch(updateExpressionAction(EText(newPageId)));
        }
      }
    };

    const pageId = ContextualExpressions.getText(stripAnn(expr));

    return <LinkPageSelectorRender pageId={pageId} onChange={onChange} />;
  },
});
